import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Banner } from '../api/types';
import { rememberCatalogPosition } from '../lib/gameLaunch';

interface Props {
  banners: Banner[];
  loading?: boolean;
  autoplayMs?: number;
}

function resolveHref(banner: Banner): string | null {
  if (banner.action_type === 'none') return null;
  if (banner.action_type === 'open_game' && banner.game_public_id) {
    return `/play/${banner.game_public_id}`;
  }
  if (banner.action_type === 'campaign_info') {
    return `/campaigns/${banner.slug}`;
  }
  if (banner.link_url?.startsWith('/')) return banner.link_url;
  return null;
}

export function BannerCarousel({ banners, loading, autoplayMs = 5000 }: Props) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const heroBanners = banners.filter((b) => (b.banner_type || 'hero') === 'hero');

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const bannerIdentity = heroBanners
    .map((b) => `${b.id}:${b.title}:${b.subtitle}:${b.cta}:${b.image_url || ''}`)
    .join('|');

  useEffect(() => {
    setIndex(0);
  }, [bannerIdentity]);

  useEffect(() => {
    if (reducedMotion || paused || heroBanners.length < 2) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % heroBanners.length);
    }, Math.max(2000, autoplayMs));
    return () => window.clearInterval(timer);
  }, [autoplayMs, heroBanners.length, paused, reducedMotion]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const slide = el.children[index] as HTMLElement | undefined;
    if (!slide) return;
    el.scrollTo({
      left: slide.offsetLeft,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }, [index, reducedMotion]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let settle: number | undefined;
    const onScroll = () => {
      window.clearTimeout(settle);
      settle = window.setTimeout(() => {
        const children = Array.from(el.children) as HTMLElement[];
        if (!children.length) return;
        const mid = el.scrollLeft + el.clientWidth / 2;
        let best = 0;
        let bestDist = Number.POSITIVE_INFINITY;
        children.forEach((child, i) => {
          const center = child.offsetLeft + child.clientWidth / 2;
          const dist = Math.abs(center - mid);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        });
        setIndex(best);
      }, 80);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.clearTimeout(settle);
      el.removeEventListener('scroll', onScroll);
    };
  }, [heroBanners.length]);

  function go(dir: -1 | 1) {
    if (heroBanners.length < 2) return;
    setPaused(true);
    setIndex((prev) => (prev + dir + heroBanners.length) % heroBanners.length);
    window.setTimeout(() => setPaused(false), autoplayMs);
  }

  if (loading) {
    return (
      <div className="hero-carousel" aria-hidden="true">
        <div className="hero-carousel-skeleton skeleton" />
      </div>
    );
  }

  if (!heroBanners.length) return null;

  return (
    <div
      className="hero-carousel"
      onPointerDown={() => setPaused(true)}
      onPointerUp={() => setPaused(false)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="hero-carousel-track" ref={trackRef} role="list">
        {heroBanners.map((banner) => {
          const href = resolveHref(banner);
          const showText =
            Boolean(banner.show_overlay_text) &&
            Boolean(banner.title || banner.subtitle || banner.cta);
          const slideClass = `hero-carousel-slide${showText ? ' hero-carousel-copy-force-cover' : ''}`;
          const img = (
            <>
              {banner.image_url ? (
                <picture>
                  {banner.image_url_desktop ? (
                    <source media="(min-width: 900px)" srcSet={banner.image_url_desktop} />
                  ) : null}
                  <img
                    src={banner.image_url}
                    alt={showText ? '' : banner.title || ''}
                    className="hero-carousel-art"
                    loading="lazy"
                    decoding="async"
                    onError={(event) => {
                      (event.currentTarget as HTMLImageElement).style.display = 'none';
                      event.currentTarget.parentElement?.classList.add('hero-carousel-fallback');
                    }}
                  />
                </picture>
              ) : (
                <div className="hero-carousel-fallback" />
              )}
              {showText ? (
                <div className="hero-carousel-copy">
                  {banner.title ? <h3>{banner.title}</h3> : null}
                  {banner.subtitle ? <p>{banner.subtitle}</p> : null}
                  {banner.cta ? <span className="hero-carousel-cta">{banner.cta}</span> : null}
                </div>
              ) : null}
            </>
          );

          if (!href) {
            return (
              <article key={banner.id || banner.slug} className={slideClass} role="listitem">
                {img}
              </article>
            );
          }

          if (href.startsWith('/play/')) {
            return (
              <button
                key={banner.id || banner.slug}
                type="button"
                className={`${slideClass} hero-carousel-slide-btn`}
                role="listitem"
                onClick={() => {
                  rememberCatalogPosition('/');
                  navigate(href);
                }}
                aria-label={banner.title || t('heroCta')}
              >
                {img}
              </button>
            );
          }

          return (
            <Link
              key={banner.id || banner.slug}
              to={href}
              className={slideClass}
              role="listitem"
              aria-label={banner.title || t('heroCta')}
            >
              {img}
            </Link>
          );
        })}
      </div>

      {heroBanners.length > 1 ? (
        <>
          <div className="hero-carousel-nav" aria-hidden={false}>
            <button type="button" className="hero-carousel-arrow" aria-label="Prev" onClick={() => go(-1)}>
              ‹
            </button>
            <button type="button" className="hero-carousel-arrow" aria-label="Next" onClick={() => go(1)}>
              ›
            </button>
          </div>
          <div className="hero-carousel-dots" aria-hidden="true">
            {heroBanners.map((banner, i) => (
              <button
                key={banner.id || banner.slug}
                type="button"
                className={`hero-carousel-dot${i === index ? ' active' : ''}`}
                onClick={() => {
                  setPaused(true);
                  setIndex(i);
                  window.setTimeout(() => setPaused(false), autoplayMs);
                }}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
